<template>
  <template v-if="sidebarComponents.length === 0">
    <section
      v-if="variants.v1 === variant"
      class="layout-pt-lg layout-pb-lg"
      :style="`background-color: var(--c-${vars.backgroundColor}); color: ${vars.textColor ? `var(--c-${vars.textColor})` : 'var(--c-text-primary)'}`"
    >
      <div class="container">
        <BlockSymbol
          :position-text="String(vars?.blockPositionText || '')"
          :block-color="String(vars?.blockColor || '')"
          :block-opacity="Number(vars?.blockOpacityNumber || 1)"
        />
        <div class="">
          <div class="row justify-between items-center">
            <div class="col-auto" v-html="vars.titleHtml"></div>

            <div v-if="vars.descriptionText" class="col-xl-6 md:d-none">
              <p class="!tw-text-white">{{ vars.descriptionText }}</p>
            </div>
          </div>
        </div>
        <div v-if="isBlogsLoading">
          <loader loader-color="var(--c-primary)" />
        </div>
        <div
          class="relative mt-60 md:mt-40 sm:mt-20 js-section-slider tw-z-10"
          data-anim="slide-left delay-1"
          data-gap="30"
          data-slider-cols="xl-3 lg-2 md-1 sm-1 base-1"
        >
          <div class="swiper-wrapper">
            <div class="swiper-slide" v-for="(blog, index) in blogs" :key="`latestblogs-blog-${index}`">
              <Theme7BlogsBlogCard :blog="blog" :index="index" :component="component" />
            </div>
          </div>

          <div class="d-flex mt-60 md:mt-30 max-lg:tw-items-center max-lg:tw-justify-center min-[1200px]:!tw-hidden">
            <button
              class="sliderNav -prev js-prev n-link !tw-bg-transparent !tw-text-white"
              style="border-color: white !important"
            >
              <i class="icon-left-arrow"></i>
            </button>
            <button
              style="border-color: white !important"
              class="sliderNav -next ml-20 js-next n-link !tw-bg-transparent !tw-text-white"
            >
              <i class="icon-right-arrow"></i>
            </button>
          </div>
        </div>
      </div>
    </section>

    <section
      v-if="variants.v2 === variant"
      class="layout-pt-lg layout-pb-lg"
      :style="`background-color: var(--c-${vars.backgroundColor}); color: ${vars.textColor ? `var(--c-${vars.textColor})` : 'var(--c-text-primary)'}`"
    >
      <div class="container">
        <BlockSymbol
          :position-text="String(vars?.blockPositionText || '')"
          :block-color="String(vars?.blockColor || '')"
          :block-opacity="Number(vars?.blockOpacityNumber || 1)"
        />

        <!-- Başlık + Açıklama -->
        <div class="row justify-between items-center tw-gap-8">
          <div class="col-auto" v-html="vars.titleHtml"></div>
          <div v-if="vars.descriptionText" class="col-xl-6">
            <p class="!tw-text-white">{{ vars.descriptionText }}</p>
          </div>
        </div>

        <div
          class="relative mt-60 md:mt-40 sm:mt-20 js-section-slider tw-z-10"
          data-anim="slide-left delay-1"
          data-gap="30"
          data-slider-cols="xl-3 lg-2 md-1 sm-1 base-1"
        >
          <div class="swiper-wrapper">
            <div class="swiper-slide" v-for="(card, index) in cards" :key="`manual-card-${index}`">
              <nuxt-link :to="`/blogs/${card.url}`" :external="true" class="!tw-p-0">
                <div class="tw-relative">
                  <library-image :src="card.photo" :title="card.title" :alt="card.title" :w-ratio="16" :h-ratio="9" />
                  <div
                    class="shine tw-absolute tw-inset-0 tw-bg-black tw-opacity-0 tw-transition-opacity tw-duration-500 group-hover:tw-opacity-20"
                    style="border-radius: var(--rounded)"
                  ></div>
                </div>
                <h4 class="tw-mt-8 tw-line-clamp-3 !tw-text-white">
                  {{ card.title }}
                </h4>
                <p class="tw-mt-4 tw-line-clamp-3 !tw-text-white">
                  {{ card.description }}
                </p>
                <button class="n-quaternary outlined tw-mt-4 !tw-justify-start">
                  VIEW MORE
                  <span class="btn-arrow"></span>
                </button>
              </nuxt-link>
            </div>
          </div>

          <div class="d-flex mt-60 md:mt-30 max-lg:tw-items-center max-lg:tw-justify-center min-[1200px]:!tw-hidden">
            <button
              class="sliderNav -prev js-prev n-link !tw-bg-transparent !tw-text-white"
              style="border-color: white !important"
            >
              <i class="icon-left-arrow"></i>
            </button>
            <button
              class="sliderNav -next ml-20 js-next n-link !tw-bg-transparent !tw-text-white"
              style="border-color: white !important"
            >
              <i class="icon-right-arrow"></i>
            </button>
          </div>
        </div>
      </div>
    </section>
  </template>

  <template v-else-if="sidebarComponents.length > 0">
    <div class="tw-px-4 tw-py-9" :style="`background-color: var(--c-${vars.backgroundColor}); border-radius: var(--rounded); border: 1px solid var(--c-border);`">
      <h4>{{ vars.titleText }}</h4>
      <div class="tw-relative tw-flex tw-h-full tw-w-full tw-flex-col tw-gap-10">
        <div
          class="relative js-section-slider"
          data-anim="slide-left delay-1"
          data-gap="30"
          data-slider-cols="xl-1 lg-1 md-1 sm-1 base-1"
        >
        <Swiper
            :key="'blog-swiper-' + blogs.length"
            :modules="swiperModules"
            :pagination="false"
            :slides-per-view="1"
            :space-between="20"
            effect="fade"
            :clickable="true"
            :watch-overflow="true"
            :loop="true"
            @slideChange="onSlideChange"
            @swiper="onSwiper"
            class="tw-w-full"
          >
            <SwiperSlide v-for="(blog, index) in blogs" :key="`latestblogs-blog-${index}`">
              <Theme7BlogsBlogCard :blog="blog" :index="index" :component="component" :variables="vars" />
            </SwiperSlide>
          </Swiper>
            
          <div class="d-flex mt-60 md:mt-30 max-lg:tw-items-center max-lg:tw-justify-center">
            <button class="sliderNav -prev  n-link" @click="slidePrev">
              <i class="icon-left-arrow"></i>
            </button>
            <button class="sliderNav -next ml-20  n-link" @click="slideNext">
              <i class="icon-right-arrow"></i>
            </button>
          </div>
        </div>
      </div>
    </div>
  </template>
</template>

<script lang="ts">
import { mapState } from 'pinia';
import type { Component } from '~/types';
import indexGlobal from '~/mixins/index.global';
import { BlogsMixin } from '~/units/blogs/mixins/blogs.mixin';
import { ComponentMixin } from '~/mixins/component.mixin';
import { useBlogsStore } from '~/units/blogs/store';
import { useWebsiteStore } from '~/units/website/store';
import { ROUTES } from '~/constants/ui.constants';
import Loader from '~/components/common/Loader.vue';
import BlockSymbol from '~/components/common/BlockSymbol.vue';
export default defineNuxtComponent({
  name: 'Theme7OurBlog',

  mixins: [BlogsMixin, indexGlobal, ComponentMixin],

  components: {
    Loader,
    BlockSymbol,
  },

  data() {
    return {
      swiper: null,
      canPaginateBack: true,
      canPaginateNext: true,

      variants: {
        v1: 'v1',
        v2: 'v2',
      },
    };
  },

  async setup(props: { component: Component }) {
    const blogsStore = useBlogsStore();
    await blogsStore.fetchBlogsInPage(props.component);
  },

  computed: {
    ...mapState(useWebsiteStore, ['sidebarComponents']),
    ROUTES() {
      return ROUTES;
    },
    cards() {
      return this.groupedVariables.cards;
    },
  },

  methods: {
    onSwiper(swiper) {
      this.swiper = swiper;
    },

    onSlideChange() {
      if (!this.swiper) return;
      const isAtStart = this.swiper.isBeginning;
      const isAtEnd = this.swiper.isEnd;
      if (isAtStart) this.canPaginateBack = false;
      else this.canPaginateBack = true;
      if (isAtEnd) this.canPaginateNext = false;
      else this.canPaginateNext = true;
    },

    slidePrev() {
      try {
        if (this.swiper) {
          this.swiper.slidePrev();
        }
      } catch (error) {
        console.error('Error in slidePrev:', error);
      }
    },

    slideNext() {
      try {
        if (this.swiper) {
          this.swiper.slideNext();
        }
      } catch (error) {
        console.error('Error in slideNext:', error);
      }
    }
  },
  mounted() {
    this.onSlideChange();
  },
});
</script>
<style scoped>
.block {
  font-size: 32rem !important;
  font-weight: 700 !important;
  color: var(--c-secondary);
  line-height: 0.8;
  z-index: -1 !important;
  margin: 0;
  padding: 0;
  position: absolute;
  bottom: 0 !important;
  left: -80px !important;
}
</style>
